import { MultiLocationEsicUpdateRequest } from 'api/companyOnboarding';
import { GetMeApiSchemaContract } from 'schemas/GetMeApiSchema';
import { uniqueId } from 'utils/Numbers';

export type OrgEsicCredentials =
  | NonNullable<
      NonNullable<GetMeApiSchemaContract['currentOrganization']>['externalCredentials']
    >['esicLocationCredentials']
  | [];
export type CredentialsFormData = {
  id: string;
  officeLocationId?: string;
  esicUsername?: string;
  esicPassword?: string;
}[];

export const getInitialCredsValue = () => ({
  id: uniqueId(),
  officeLocationId: '',
  esicUsername: '',
  esicPassword: '',
});

export const getCredentialsInitialValue = (
  multiCredentials: OrgEsicCredentials,
): CredentialsFormData => {
  if (Object.keys(multiCredentials || {}).length === 0) {
    return [getInitialCredsValue()];
  }
  const credentials = Object.values(multiCredentials || {});
  const formData =
    credentials.map((cred) => ({
      id: cred.locationId ? cred.locationId.toString() : uniqueId(),
      officeLocationId: cred.locationId ? cred.locationId.toString() : '',
      esicUsername: cred.esicUser ?? '',
      esicPassword: cred.esicPass ?? '',
    })) || [];

  formData.push(getInitialCredsValue());
  return formData;
};

export const validateCredentials = (data: CredentialsFormData) => {
  const credentials = Object.values(data || {});
  const errors: Array<string> = [];
  // Build a frequency map of ids to detect duplicates
  const locationIdCount: Record<string, number> = {};

  credentials.forEach((cred, idx) => {
    let hasError = false;
    let errorString = `Error in Row ${idx + 1}: `;

    if (!cred.officeLocationId && (cred.esicPassword || cred.esicUsername)) {
      errorString += 'Provide Office Location, or clear Username & Password fields';
      hasError = true;
    } else if (
      cred.officeLocationId &&
      (cred.esicUsername || cred.esicPassword) &&
      !(cred.esicUsername && cred.esicPassword)
    ) {
      errorString += 'Please provide Username and Password, or leave both fields empty';
      hasError = true;
    }

    if (cred.officeLocationId && cred.esicPassword && cred.esicUsername) {
      const locationId = cred.officeLocationId;
      locationIdCount[locationId] = (locationIdCount[locationId] || 0) + 1;
      if (locationIdCount[locationId] > 1) {
        errorString += 'Duplicate Office Location detected. Each location can only be added once';
        hasError = true;
      }
    }

    if (hasError) {
      errors.push(errorString);
    }
  });

  return errors.join(', ');
};

export const getCredentialsPayload = (
  data: CredentialsFormData,
): MultiLocationEsicUpdateRequest['esicCredentials'] => {
  const credentials = Object.values(data || {});
  return credentials
    .filter((cred) => cred.officeLocationId && cred.esicUsername && cred.esicPassword)
    .map((cred) => ({
      location_id: cred.officeLocationId ? Number(cred.officeLocationId) : 0,
      esi_username: cred.esicUsername ?? '',
      esi_password: cred.esicPassword ?? '',
    }));
};
